import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Handshake, CheckCircle2 } from "lucide-react";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useToast } from "@/hooks/use-toast";

export default function Commitments() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["commitments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("commitments")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  const close = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("commitments").update({ status: "done" }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commitments"] });
      toast({ title: "Compromisso concluído" });
    },
  });

  const statusVariant = (s: string) =>
    s === "done" ? "default" : s === "overdue" ? "destructive" : "secondary";

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Compromissos" }]} />
      <div>
        <h1 className="text-2xl font-bold font-headline flex items-center gap-2"><Handshake className="w-6 h-6 text-primary" /> Compromissos</h1>
        <p className="text-sm text-muted-foreground">Promessas e tarefas captadas automaticamente das conversas.</p>
      </div>
      <Card>
        <CardHeader><CardTitle>{items.length} compromissos</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Nenhum compromisso registrado.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Responsável</TableHead>
                  <TableHead>Compromisso</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead>Confiança</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm">{c.owner_name ?? c.owner_jid ?? "—"}</TableCell>
                    <TableCell className="text-sm max-w-md">{c.subject}</TableCell>
                    <TableCell className="text-xs">{c.due_at ? new Date(c.due_at).toLocaleString("pt-BR") : "—"}</TableCell>
                    <TableCell className="text-xs font-mono">{c.confidence != null ? `${(c.confidence * 100).toFixed(0)}%` : "—"}</TableCell>
                    <TableCell><Badge variant={statusVariant(c.status) as never}>{c.status}</Badge></TableCell>
                    <TableCell>
                      {c.status !== "done" && (
                        <Button size="sm" variant="ghost" onClick={() => close.mutate(c.id)}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Concluir
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
